import { useCallback, useEffect, useState } from "react";
import { Check, Copy, MailOpen, ShieldOff, UserCheck, Users } from "lucide-react";
import { C } from "../theme";
import { assignableRoleSlugs, canEditMemberRole, canManageTeam, canRevokeMember } from "../lib/access";
import { currentUserIdFromToken } from "../lib/jwt";
import { ApiError } from "../lib/api/client";
import { listTenantInvites, listTenantMembers, removeTenantMember, updateMemberRole } from "../lib/api/endpoints";
import { INVITE_STATUS, inviteStatusLabel } from "../lib/enums";
import { TEAM_ROLES } from "../lib/roles";
import { roleLabel } from "../lib/teamRoles";
import { useSession } from "../lib/session/SessionContext";
import type { TenantInviteDto, TenantMemberDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { Label } from "./Label";
import { Select, type SelectOption } from "./Select";
import { TeamInvitePanel } from "./TeamInvitePanel";

function formatCreatedAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("pt-BR");
}

function formatExpiry(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("pt-BR");
}

export function TeamTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const actorRole = activeTenant?.roleSlug ?? null;
  const canManage = canManageTeam(actorRole);
  const currentUserId = currentUserIdFromToken(token);

  const [members, setMembers] = useState<TenantMemberDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [invites, setInvites] = useState<TenantInviteDto[] | null>(null);
  const [invitesError, setInvitesError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [changingRoleId, setChangingRoleId] = useState<string | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<TenantMemberDto | null>(null);
  const [revoking, setRevoking] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!token) return;
    setLoadError(null);
    try {
      setMembers(await listTenantMembers(token));
    } catch (e) {
      setMembers(null);
      setLoadError(e instanceof ApiError ? (e.detail ?? "Não foi possível carregar a equipe.") : "Não foi possível carregar a equipe.");
    }
  }, [token]);

  const loadInvites = useCallback(async () => {
    if (!token) return;
    setInvitesError(null);
    try {
      setInvites(await listTenantInvites(token));
    } catch (e) {
      setInvites(null);
      setInvitesError(e instanceof ApiError ? (e.detail ?? "Não foi possível carregar os convites.") : "Não foi possível carregar os convites.");
    }
  }, [token]);

  useEffect(() => {
    void loadMembers();
    void loadInvites();
  }, [loadMembers, loadInvites]);

  if (!canManage) {
    return (
      <Card>
        <Label style={{ color: C.red }}>Acesso restrito</Label>
        <p style={{ fontSize: 13, color: C.gray, margin: "8px 0" }}>A gestão de equipe é restrita a owner/admin do CT.</p>
      </Card>
    );
  }

  const assignableRoles = TEAM_ROLES.filter(r => assignableRoleSlugs(actorRole).includes(r.slug));
  const roleOptions: SelectOption[] = assignableRoles.map(r => ({ value: r.id, label: r.label }));

  const handleRoleChange = async (member: TenantMemberDto, roleId: string) => {
    if (!token || changingRoleId) return;
    setActionError(null);
    setSuccessMessage(null);
    setChangingRoleId(member.userId);
    try {
      await updateMemberRole(token, member.userId, roleId);
      setMembers(prev => prev?.map(m => (m.userId === member.userId ? { ...m, roleId, roleSlug: roleLabel(roleId).toLowerCase() } : m)) ?? prev);
      setSuccessMessage(`Papel de ${member.displayName} atualizado para ${roleLabel(roleId)}.`);
    } catch (e) {
      setActionError(e instanceof ApiError ? (e.detail ?? "Não foi possível alterar o papel.") : "Não foi possível alterar o papel.");
    } finally {
      setChangingRoleId(null);
    }
  };

  const handleRevoke = async () => {
    if (!token || !pendingRevoke || revoking) return;
    setRevoking(true);
    setActionError(null);
    setSuccessMessage(null);
    try {
      await removeTenantMember(token, pendingRevoke.userId);
      setMembers(prev => prev?.filter(m => m.userId !== pendingRevoke.userId) ?? prev);
      setSuccessMessage(`Acesso de ${pendingRevoke.displayName} revogado.`);
      setPendingRevoke(null);
    } catch (e) {
      setActionError(e instanceof ApiError ? (e.detail ?? "Não foi possível revogar o acesso.") : "Não foi possível revogar o acesso.");
      setPendingRevoke(null);
    } finally {
      setRevoking(false);
    }
  };

  const copyInviteLink = async (invite: TenantInviteDto) => {
    const link = `${window.location.origin}${invite.link}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // clipboard pode não estar disponível fora de contexto seguro; o link fica visível
    }
    setCopiedCode(invite.code);
    window.setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {actionError && (
        <div style={{ fontSize: 12.5, color: C.red, background: "C.redSoft", borderRadius: 8, padding: "8px 12px" }}>{actionError}</div>
      )}
      {successMessage && (
        <div style={{ fontSize: 12.5, color: C.ink, background: C.limeSoft, borderRadius: 8, padding: "8px 12px" }}>{successMessage}</div>
      )}

      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Users size={15} style={{ color: C.gray }} />
          <Label>Equipe do CT</Label>
        </div>
        {loadError ? (
          <div style={{ color: C.red, fontSize: 13, padding: "18px 0" }}>{loadError}</div>
        ) : members === null ? (
          <div style={{ color: C.light, fontSize: 13, padding: "24px 0" }}>Carregando equipe...</div>
        ) : members.length === 0 ? (
          <div style={{ color: C.light, fontSize: 13, padding: "24px 0" }}>Nenhum funcionário vinculado ainda.</div>
        ) : (
          <div style={{ overflowX: "auto", marginTop: 14 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640, fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: "left", color: C.light }}>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Funcionário</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Papel</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Desde</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}`, textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => {
                  const isSelf = member.userId === currentUserId;
                  const canEdit = canEditMemberRole(actorRole, member.roleSlug);
                  const canRevoke = canRevokeMember(actorRole, member.roleSlug, isSelf);
                  return (
                    <tr key={member.userId}>
                      <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}` }}>
                        <span style={{ color: C.ink, fontWeight: 600 }}>{member.displayName}</span>
                        {isSelf && <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: C.ink, background: C.limeSoft, padding: "2px 8px", borderRadius: 20, marginLeft: 8, textTransform: "uppercase" }}>você</span>}
                      </td>
                      <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}` }}>
                        {canEdit ? (
                          <div style={{ minWidth: 150 }}>
                            <Select
                              id={`role-${member.userId}`}
                              value={member.roleId}
                              options={roleOptions}
                              onChange={value => handleRoleChange(member, value)}
                              disabled={changingRoleId === member.userId}
                              ariaLabel={`Papel de ${member.displayName}`}
                            />
                          </div>
                        ) : (
                          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: C.ink, background: C.limeSoft, padding: "3px 9px", borderRadius: 20, textTransform: "uppercase" }}>
                            {member.roleSlug}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, color: C.gray }}>{formatCreatedAt(member.createdAt)}</td>
                      <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, textAlign: "right", whiteSpace: "nowrap" }}>
                        {canRevoke && (
                          <Button
                            variant="ghost"
                            ariaLabel={`Revogar acesso de ${member.displayName}`}
                            title={`Revogar acesso de ${member.displayName}`}
                            onClick={() => {
                              setActionError(null);
                              setSuccessMessage(null);
                              setPendingRevoke(member);
                            }}
                            style={{ color: C.red, padding: 6, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}
                          >
                            <ShieldOff size={14} aria-hidden="true" /> Revogar
                          </Button>
                        )}
                        {!canRevoke && !isSelf && (
                          <span style={{ fontSize: 11, color: C.light }}>
                            <UserCheck size={13} style={{ verticalAlign: -2 }} aria-hidden="true" /> protegido
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <MailOpen size={15} style={{ color: C.gray }} />
          <Label>Convites</Label>
        </div>
        {invitesError ? (
          <div style={{ color: C.red, fontSize: 13, padding: "18px 0" }}>{invitesError}</div>
        ) : invites === null ? (
          <div style={{ color: C.light, fontSize: 13, padding: "18px 0" }}>Carregando convites...</div>
        ) : invites.length === 0 ? (
          <div style={{ color: C.light, fontSize: 13, padding: "18px 0" }}>Nenhum convite gerado ainda.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {invites.map(inv => {
              const pending = inv.status === INVITE_STATUS.Pending;
              return (
                <div key={inv.id} style={{ display: "flex", flexDirection: "column", gap: 6, border: `1px solid ${C.faint}`, borderRadius: 10, padding: "10px 12px", opacity: pending ? 1 : 0.6 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: C.ink }}>{inv.email}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: C.ink, background: C.limeSoft, padding: "3px 9px", borderRadius: 20, textTransform: "uppercase" }}>
                        {inv.roleSlug || roleLabel(inv.roleId) || "—"}
                      </span>
                      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: pending ? C.gray : C.light, padding: "3px 9px", borderRadius: 20, textTransform: "uppercase" }}>
                        {inviteStatusLabel(inv.status)}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <code style={{ flex: 1, fontSize: 11.5, color: C.gray, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {window.location.origin}{inv.link}
                    </code>
                    <span style={{ fontSize: 11, color: C.light, whiteSpace: "nowrap" }}>expira em {formatExpiry(inv.expiresAtUtc)}</span>
                    <button
                      type="button"
                      onClick={() => copyInviteLink(inv)}
                      aria-label={`Copiar link de ${inv.email}`}
                      style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: C.ink, fontFamily: "inherit", fontSize: 11.5, fontWeight: 600, padding: 4 }}
                    >
                      {copiedCode === inv.code ? <Check size={14} style={{ color: C.ink }} /> : <Copy size={14} />}
                      {copiedCode === inv.code ? "Copiado" : "Copiar"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card>
        <div style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600, marginBottom: 10 }}>CONVIDAR FUNCIONÁRIO</div>
        <TeamInvitePanel roles={assignableRoles} hideInvites onInvite={() => void loadInvites()} />
      </Card>

      {pendingRevoke && (
        <ConfirmDialog
          title="Revogar acesso"
          confirmLabel="Revogar"
          busy={revoking}
          busyLabel="Revogando..."
          onConfirm={handleRevoke}
          onCancel={() => setPendingRevoke(null)}
        >
          Tem certeza que deseja remover o acesso de <b style={{ color: C.ink }}>&quot;{pendingRevoke.displayName}&quot;</b> ao CT? O funcionário deixará de acessar o painel.
        </ConfirmDialog>
      )}
    </div>
  );
}